
import { useTranslation } from 'react-i18next';

interface Property {
  id: string;
  title: string;
  price: number;
  sale_price?: number;
  rent_price?: number;
  currency?: string;
  location: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  property_type: string;
  status: string;
  available_for_sale?: boolean;
  available_for_rent?: boolean;
  images: string[];
  description: string;
  features: string[];
  year_built?: number;
  parking?: number;
  floor?: number;
  furnished?: boolean;
  city: string;
  address?: string;
  videos?: string[];
  latitude?: number;
  longitude?: number;
  is_featured?: boolean;
  maintenance_fee?: number;
  has_maintenance_fee?: boolean;
  included_services?: string[];
}

interface PropertyCompareProps {
  properties: Property[];
  onRemoveFromCompare: (propertyId: string) => void;
  onClose: () => void;
}

export default function PropertyCompare({ properties, onRemoveFromCompare, onClose }: PropertyCompareProps) {
  const { t } = useTranslation();

  const formatPrice = (price: number, currency: string = 'MXN') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    } else {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    }
  };

  const getPropertyTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'casa': 'Casa',
      'oficina': 'Oficina',
      'local_comercial': 'Local Comercial',
      'terreno': 'Terreno',
      'apartamento': 'Apartamento'
    };
    return typeMap[type] || type;
  };

  const getAvailabilityBadges = (property: Property) => {
    const badges = [];
    
    if (property.available_for_sale) {
      badges.push('Venta');
    }
    
    if (property.available_for_rent) {
      badges.push('Renta');
    }
    
    if (badges.length === 0) {
      badges.push(property.status === 'rented' ? 'Renta' : 'Venta');
    }
    
    return badges;
  };

  const getServiceLabels = (services: string[]) => {
    const serviceMap: { [key: string]: string } = {
      'internet': 'Internet',
      'mantenimiento': 'Mantenimiento',
      'agua': 'Agua',
      'gas': 'Gas'
    };
    
    return services.map(service => serviceMap[service] || service);
  };

  if (properties.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">
            <i className="ri-scales-3-line mr-2"></i>
            {t('compare')} ({properties.length} {t('properties')})
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Comparison Table */}
        <div className="overflow-auto max-h-[calc(95vh-120px)]">
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-4 border-b border-gray-200 bg-gray-50 font-semibold text-gray-700 min-w-[200px]">
                      Característica
                    </th>
                    {properties.map((property) => (
                      <th key={property.id} className="text-center p-4 border-b border-gray-200 bg-gray-50 min-w-[250px]">
                        <div className="space-y-2">
                          <div className="relative">
                            <img
                              src={property.images[0]}
                              alt={property.title}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <button
                              onClick={() => onRemoveFromCompare(property.id)}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                              title="Eliminar de comparación"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                          <div className="text-sm font-medium text-gray-800 line-clamp-2">
                            {property.title}
                          </div>
                          <div className="flex flex-wrap gap-1 justify-center">
                            {getAvailabilityBadges(property).map((badge, index) => (
                              <span 
                                key={index}
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  badge === 'Venta' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {badge}
                              </span>
                            ))}
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Precio de Venta */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-price-tag-3-line mr-2 text-green-600"></i>
                      Precio de Venta
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        {property.available_for_sale ? (
                          <div className="text-lg font-bold text-green-600">
                            {formatPrice(property.sale_price || property.price || 0, property.currency)}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">No disponible</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Precio de Renta */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-calendar-line mr-2 text-blue-600"></i>
                      Precio de Renta
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        {property.available_for_rent ? (
                          <div className="text-lg font-bold text-blue-600">
                            {formatPrice(property.rent_price || property.price || 0, property.currency)}/mes
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">No disponible</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Cuota de Mantenimiento */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-tools-line mr-2 text-purple-600"></i>
                      Cuota de Mantenimiento
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        {property.available_for_rent && property.has_maintenance_fee && property.maintenance_fee ? (
                          <div className="text-sm font-semibold text-purple-600">
                            {formatPrice(property.maintenance_fee, property.currency)}/mes
                          </div>
                        ) : property.available_for_rent ? (
                          <span className="text-green-600 text-sm">
                            <i className="ri-check-line mr-1"></i>
                            Sin cuota
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">No aplica</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Servicios Incluidos */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-checkbox-multiple-line mr-2 text-green-600"></i>
                      Servicios Incluidos
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        {property.available_for_rent && property.included_services && property.included_services.length > 0 ? (
                          <div className="space-y-1">
                            {getServiceLabels(property.included_services).map((service, index) => (
                              <div key={index} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full inline-block mr-1 mb-1">
                                {service}
                              </div>
                            ))}
                          </div>
                        ) : property.available_for_rent ? (
                          <span className="text-gray-500 text-sm">Sin servicios incluidos</span>
                        ) : (
                          <span className="text-gray-400 text-sm">No aplica</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Tipo de Propiedad */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-home-line mr-2"></i>
                      Tipo
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        <span className="text-gray-800">{getPropertyTypeLabel(property.property_type)}</span>
                      </td>
                    ))}
                  </tr>

                  {/* Ubicación */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-map-pin-line mr-2"></i>
                      Ubicación
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        <span className="text-gray-800 text-sm">{property.location}</span>
                      </td>
                    ))}
                  </tr>

                  {/* Habitaciones */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-hotel-bed-line mr-2"></i>
                      {t('bedrooms')}
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        <span className="text-gray-800 font-semibold">
                          {property.bedrooms > 0 ? property.bedrooms : 'N/A'}
                        </span>
                      </td>
                    ))}
                  </tr>

                  {/* Baños */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-drop-line mr-2"></i>
                      {t('bathrooms')}
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        <span className="text-gray-800 font-semibold">{property.bathrooms}</span>
                      </td>
                    ))}
                  </tr>

                  {/* Área */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-square-line mr-2"></i>
                      Área
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        <span className="text-gray-800 font-semibold">{property.area} m²</span>
                      </td>
                    ))}
                  </tr>

                  {/* Estacionamiento */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-car-line mr-2"></i>
                      Estacionamiento
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        <span className="text-gray-800 font-semibold">
                          {property.parking && property.parking > 0 ? property.parking : 'N/A'}
                        </span>
                      </td>
                    ))}
                  </tr>

                  {/* Año de construcción */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-calendar-line mr-2"></i>
                      Año de construcción
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        <span className="text-gray-800">
                          {property.year_built || 'No especificado'}
                        </span>
                      </td>
                    ))}
                  </tr>

                  {/* Amueblado */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-sofa-line mr-2"></i>
                      Amueblado
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          property.furnished 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {property.furnished ? 'Sí' : 'No'}
                        </span>
                      </td>
                    ))}
                  </tr>

                  {/* Características */}
                  <tr>
                    <td className="p-4 font-medium text-gray-700 bg-gray-50">
                      <i className="ri-list-check mr-2"></i>
                      Características
                    </td>
                    {properties.map((property) => (
                      <td key={property.id} className="p-4">
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {property.features && property.features.length > 0 ? (
                            property.features.map((feature, index) => (
                              <div key={index} className="flex items-center gap-1 text-xs">
                                <i className="ri-check-line text-green-500"></i>
                                <span className="text-gray-600">{feature}</span>
                              </div>
                            ))
                          ) : (
                            <span className="text-gray-400 text-sm">Sin características especificadas</span>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Comparando {properties.length} {properties.length === 1 ? 'propiedad' : 'propiedades'}
            </div>
            <button
              onClick={onClose}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cerrar Comparación
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
